<template>
  <div>
    <Head title="Bulk Replace" />

    <h1 class="mb-8 mt-4 text-3xl font-bold">Bulk Replace</h1>

    <!-- Search panel -->
    <div class="bg-white rounded-md shadow overflow-hidden mb-6">
      <div class="p-6">
        <h2 class="text-lg font-semibold text-gray-700 mb-4">Search across all configurations</h2>
        <div class="flex gap-3 items-end">
          <div class="flex-1">
            <label class="block text-sm font-medium text-gray-600 mb-1">Find</label>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="e.g. old-domain.com"
              class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              @keyup.enter="doSearch"
            />
          </div>
          <button
            @click="doSearch"
            :disabled="searching || !searchQuery.trim()"
            class="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <span v-if="searching" class="flex items-center gap-2">
              <svg class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              Searching...
            </span>
            <span v-else>Search</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Results -->
    <div v-if="results !== null">

      <!-- Summary bar -->
      <div class="flex items-center justify-between mb-4">
        <div v-if="results.length > 0" class="text-sm text-gray-600">
          Found
          <span class="font-semibold text-gray-900">{{ totalMatches }}</span> matches in
          <span class="font-semibold text-gray-900">{{ results.length }}</span> configurations
        </div>
        <div v-else class="text-sm text-gray-500">No matches found</div>

        <div v-if="results.length > 0" class="flex gap-2">
          <button @click="selectAll" class="text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50">
            Select all
          </button>
          <button @click="deselectAll" class="text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50">
            Deselect all
          </button>
        </div>
      </div>

      <!-- Config list -->
      <div v-if="results.length > 0" class="bg-white rounded-md shadow overflow-hidden mb-6">
        <div
          v-for="config in results"
          :key="config.guid"
          class="border-b last:border-b-0"
          :class="selectedGuids.includes(config.guid) ? 'bg-indigo-50' : ''"
        >
          <div class="p-4">
            <div class="flex items-start gap-3">
              <input
                type="checkbox"
                :value="config.guid"
                v-model="selectedGuids"
                class="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 cursor-pointer"
              />
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between mb-2">
                  <div>
                    <span class="font-semibold text-gray-900">{{ config.appName }}</span>
                    <span class="ml-2 text-xs text-gray-400 font-mono">{{ config.guid }}</span>
                  </div>
                  <span class="ml-2 shrink-0 text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">
                    {{ config.matchCount }} matches
                  </span>
                </div>
                <!-- Matches -->
                <div class="space-y-1">
                  <div
                    v-for="(match, i) in config.matches"
                    :key="i"
                    class="text-sm bg-gray-50 rounded px-3 py-1.5 font-mono break-all"
                  >
                    <span class="text-gray-400 select-none">{{ match.path }}: </span>
                    <span v-html="highlight(match.value, searchQuery)" />
                  </div>
                  <div v-if="config.matchCount > config.matches.length" class="text-xs text-gray-400 pl-3">
                    … and {{ config.matchCount - config.matches.length }} more
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Replace panel -->
      <div v-if="results.length > 0 && selectedGuids.length > 0" class="bg-white rounded-md shadow overflow-hidden">
        <div class="p-6">
          <h2 class="text-lg font-semibold text-gray-700 mb-4">Replace in selected ({{ selectedGuids.length }})</h2>

          <div class="flex gap-3 items-end mb-4">
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-600 mb-1">Replace with</label>
              <input
                v-model="replaceQuery"
                type="text"
                placeholder="e.g. new-domain.com"
                class="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <button
              @click="doPreview"
              :disabled="previewing || !replaceQuery.trim()"
              class="px-4 py-2 border border-indigo-300 text-indigo-700 rounded-md hover:bg-indigo-50 disabled:opacity-50 whitespace-nowrap"
            >
              {{ previewing ? 'Loading...' : 'Preview' }}
            </button>
          </div>

          <!-- Dry run result -->
          <div v-if="previewResult" class="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
            <strong>Preview:</strong>
            <strong>{{ previewResult.totalChanges }}</strong> values will be changed in
            <strong>{{ previewResult.totalConfigs }}</strong> configurations.
            <span v-if="selectedGuids.length > 5" class="ml-1 text-blue-600">
              (more than 5 configs — will be queued via Beanstalk)
            </span>
          </div>

          <div class="flex gap-3">
            <button
              @click="confirmModal = true"
              :disabled="replacing || !replaceQuery.trim()"
              class="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Replace in selected ({{ selectedGuids.length }})
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Flash message -->
    <transition name="fade">
      <div
        v-if="flash.message"
        class="fixed bottom-6 right-6 max-w-sm p-4 rounded-lg shadow-lg text-sm z-50"
        :class="flash.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'"
      >
        <div class="font-semibold mb-1">{{ flash.type === 'success' ? 'Done' : 'Error' }}</div>
        <div>{{ flash.message }}</div>
        <div v-if="flash.errors && flash.errors.length" class="mt-2 text-xs opacity-80">
          <div v-for="err in flash.errors" :key="err">{{ err }}</div>
        </div>
      </div>
    </transition>

    <!-- Confirmation modal -->
    <div v-if="confirmModal" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black bg-opacity-40" @click="confirmModal = false" />
      <div class="relative bg-white rounded-xl shadow-xl p-8 max-w-md w-full mx-4">
        <h3 class="text-xl font-bold text-gray-900 mb-3">Confirm replacement</h3>
        <p class="text-gray-600 mb-2">
          Replace <span class="font-mono bg-red-100 text-red-700 px-1 rounded">{{ searchQuery }}</span>
          with <span class="font-mono bg-green-100 text-green-700 px-1 rounded">{{ replaceQuery }}</span>
        </p>
        <p class="text-gray-600 mb-6">
          in <strong>{{ selectedGuids.length }}</strong> selected configurations?
          <span v-if="selectedGuids.length > 5" class="block mt-1 text-sm text-orange-600">
            More than 5 configurations — the operation will be processed via queue.
          </span>
        </p>
        <div class="flex gap-3">
          <button
            @click="doReplace"
            :disabled="replacing"
            class="flex-1 py-2 bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            <span v-if="replacing">Processing...</span>
            <span v-else>Confirm</span>
          </button>
          <button
            @click="confirmModal = false"
            class="flex-1 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/vue3'
import axios from 'axios'

export default {
  components: { Head },

  data() {
    return {
      searchQuery: '',
      replaceQuery: '',
      searching: false,
      previewing: false,
      replacing: false,
      results: null,
      totalMatches: 0,
      selectedGuids: [],
      previewResult: null,
      confirmModal: false,
      flash: { message: '', type: 'success', errors: [] },
      flashTimer: null,
    }
  },

  methods: {
    async doSearch() {
      if (!this.searchQuery.trim()) return
      this.searching = true
      this.results = null
      this.selectedGuids = []
      this.previewResult = null

      try {
        const { data } = await axios.post('/api/config/bulk/search', {
          search: this.searchQuery,
        })
        this.results = data.results
        this.totalMatches = data.totalMatches
        this.selectedGuids = data.results.map((r) => r.guid)
      } catch (e) {
        this.showFlash('Search error: ' + (e.response?.data?.message || e.message), 'error')
      } finally {
        this.searching = false
      }
    },

    async doPreview() {
      if (!this.replaceQuery.trim() || !this.selectedGuids.length) return
      this.previewing = true
      this.previewResult = null

      try {
        const { data } = await axios.post('/api/config/bulk/preview', {
          search: this.searchQuery,
          replace: this.replaceQuery,
          guids: this.selectedGuids,
        })
        this.previewResult = data
      } catch (e) {
        this.showFlash('Preview error: ' + (e.response?.data?.message || e.message), 'error')
      } finally {
        this.previewing = false
      }
    },

    async doReplace() {
      this.replacing = true
      this.confirmModal = false

      try {
        const { data } = await axios.post('/api/config/bulk/replace', {
          search: this.searchQuery,
          replace: this.replaceQuery,
          guids: this.selectedGuids,
        })

        this.showFlash(data.message, 'success', data.errors)

        if (!data.queued) {
          await this.doSearch()
          this.replaceQuery = ''
          this.previewResult = null
        }
      } catch (e) {
        this.showFlash('Replace error: ' + (e.response?.data?.message || e.message), 'error')
      } finally {
        this.replacing = false
      }
    },

    selectAll() {
      this.selectedGuids = this.results.map((r) => r.guid)
    },

    deselectAll() {
      this.selectedGuids = []
    },

    highlight(text, search) {
      if (!search) return this.escapeHtml(text)
      const parts = text.split(
        new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
      )
      return parts
        .map((part, i) => {
          const escaped = this.escapeHtml(part)
          return i % 2 === 1
            ? `<mark class="bg-yellow-200 rounded px-0.5 not-italic">${escaped}</mark>`
            : escaped
        })
        .join('')
    },

    escapeHtml(str) {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },

    showFlash(message, type = 'success', errors = []) {
      clearTimeout(this.flashTimer)
      this.flash = { message, type, errors }
      this.flashTimer = setTimeout(() => {
        this.flash = { message: '', type: 'success', errors: [] }
      }, 6000)
    },
  },
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
